import React, { useEffect, useState, useCallback } from "react"
import Icon from "../../../components/AppIcon"
import { Card, CardContent } from "../../../components/ui/card"
import LearnersChart from "./analytics/LearnersChart"
import LearnerMix from "./analytics/LearnerMix"
import {
  computeLearnerIntelligence,
  getLearnerTrend,
  getLearnerMix,
} from "../../../utils/dashboardAnalyticsHelpers"
import { DATE_RANGE_OPTIONS } from "../../../utils/dateRangeHelpers"

const RANGE_SUBSET = DATE_RANGE_OPTIONS.filter((r) => ["30d", "90d", "12m"].includes(r.key))

const RateCard = ({ label, value, change, icon, suffix = "%", hint }) => (
  <Card className="p-4 dark:bg-transparent">
    <div className="flex items-center justify-between">
      <p className="text-xs text-muted-foreground">{label}</p>
      <Icon name={icon} size={14} className="text-muted-foreground" />
    </div>
    <p className="text-2xl font-bold text-foreground mt-0.5">
      {value === null ? "—" : `${value}${suffix}`}
    </p>
    {change !== null && change !== undefined ? (
      <p className={`text-xs mt-1 ${change >= 0 ? "text-success" : "text-error"}`}>
        {change >= 0 ? "+" : ""}
        {change}% vs previous period
      </p>
    ) : hint ? (
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    ) : null}
  </Card>
)

const IntelligenceLearnersPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [rangeKey, setRangeKey] = useState("30d")
  const [stats, setStats] = useState(null)
  const [trend, setTrend] = useState(null)
  const [mix, setMix] = useState([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    const [intel, learnerTrend, learnerMix] = await Promise.all([
      computeLearnerIntelligence(orgId, rangeKey),
      getLearnerTrend(orgId),
      getLearnerMix(orgId),
    ])
    setStats(intel)
    setTrend(learnerTrend)
    setMix(learnerMix)
    setLoading(false)
  }, [orgId, rangeKey])

  useEffect(() => {
    load()
  }, [load])

  if (loading || !stats) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 6 })?.map((_, i) => (
          <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-xl">
          How learners move through activation, engagement, and completion — not just how many
          there are.
        </p>
        <div className="flex gap-1 shrink-0">
          {RANGE_SUBSET.map((r) => (
            <button
              key={r.key}
              onClick={() => setRangeKey(r.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                rangeKey === r.key
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <RateCard
          label="Activation Rate"
          value={stats.activation.rate}
          change={stats.activation.change}
          icon="Zap"
        />
        <RateCard
          label="Engagement Rate"
          value={stats.engagement.rate}
          change={stats.engagement.change}
          icon="Activity"
        />
        <RateCard label="Retention Rate" value={stats.retention.rate} change={null} icon="Repeat" />
        <RateCard
          label="Completion Rate"
          value={stats.completion.rate}
          change={null}
          icon="CheckCircle2"
          hint={`${stats.completion.count} of ${stats.completion.total} enrollments`}
        />
        <RateCard
          label="Dropout Rate"
          value={stats.dropout.rate}
          change={null}
          icon="TrendingDown"
          hint={`${stats.dropout.count} enrollments stalled 30+ days`}
        />
        <RateCard
          label="Avg. Assessment Score"
          value={stats.assessmentPerformance.avgScore}
          change={null}
          icon="ClipboardCheck"
          hint={
            stats.assessmentPerformance.avgScore === null
              ? "No graded assessments yet"
              : `${stats.assessmentPerformance.totalAttempts} total attempts`
          }
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <LearnersChart data={trend?.data} deltaPercent={trend?.deltaPercent} />
        <LearnerMix
          rows={mix}
          title="Learner mix"
          description="Engagement stage across all learners"
        />
      </div>

      <Card className="p-4 dark:bg-transparent border-dashed">
        <div className="flex items-start gap-2.5">
          <Icon name="Info" size={15} className="text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground">
            Learning time per session isn't tracked yet — once session duration is recorded, average
            time-on-platform and time-to-completion will appear here.
          </p>
        </div>
      </Card>
    </div>
  )
}

export default IntelligenceLearnersPanel